import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import WarningPage from './WarningPage';
import RetryNotice from './RetryNotice';
import apiRequest from '../../api/request';
import { RULES, numberField, validateNumber } from './thresholdValidation';
import { downloadCSV, makeCSV } from '../../components/CustomTableWithPagination/csvExport';
import SetBy from './SetBy';
import dayjs from 'dayjs';

const PHASE_LABEL = { 1: 'Onboarding', 2: 'Demo & Admission', 3: 'Tracking & Retention' };
const CSV_HEADERS = [
  { field: 'phase_no', headerName: 'Phase', valueGetter: ({ value }) => PHASE_LABEL[value] || `Phase ${value}` },
  { field: 'short_name', headerName: 'Stage' },
  { field: 'threshold_hours', headerName: 'Threshold (hours)' },
  { field: 'is_configured', headerName: 'Configured', valueGetter: ({ value }) => (value ? 'Yes' : 'Default') },
];

// CRO Pending Tasks' threshold config - one fixed row per workflow stage (no add/remove, so no popup, no
// inline add-panel either - just an editable number per row with its own Save button).
const CroPendingTasksConfig = () => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingStage, setSavingStage] = useState(null);
  // stages whose Save was clicked - a blank box only complains after that
  const [attempted, setAttempted] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = () => {
    setLoading(true);
    apiRequest({ url: 'warning-dashboard/cro-pending-tasks-config', method: 'get' })
      .then((res) => {
        // apiRequest resolves even on a failed call (src/api/request.js), so this status check is required -
        // a .catch alone would never fire and this would silently show an empty table instead of the error.
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load thresholds');
          return;
        }
        const data = res?.data?.rows || [];
        setRows(data);
        setDrafts(Object.fromEntries(data.map((r) => [r.stage_no, r.threshold_hours])));
        setError(null);
        // Table still comes back with all 13 stages at their fallback value even when the config table hasn't
        // been created yet - this is informational, not a reason to hide the table.
        setNotice(res?.data?.schemaMissing ? res.data.schemaMissingMessage : null);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Could not load thresholds'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = (stageNo) => {
    // BUG-03: this used to return without a word when the box was blank, zero or negative.
    // Whole hours only (the column is an integer), greater than 0.
    setAttempted((prev) => ({ ...prev, [stageNo]: true }));
    if (validateNumber(drafts[stageNo], 'Threshold', RULES.positiveHours)) return;
    const thresholdHours = Number(drafts[stageNo]);
    setSavingStage(stageNo);
    apiRequest({ url: 'warning-dashboard/cro-pending-tasks-config', method: 'post', data: { stage_no: stageNo, threshold_hours: thresholdHours } })
      .then((res) => {
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not save threshold');
          return;
        }
        load();
      })
      .catch((err) => setError(err?.response?.data?.message || 'Could not save threshold'))
      .finally(() => setSavingStage(null));
  };

  const handleExport = () => {
    const csv = makeCSV({ headers: CSV_HEADERS, rows });
    downloadCSV(csv, `CRO_Pending_Tasks_Thresholds_${dayjs().format('YYYY-MM-DD_HH-mm')}.csv`);
  };

  return (
    <WarningPage
      title="CRO Pending Tasks - Thresholds"
      actions={
        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={handleExport} disabled={loading || !!error || rows.length === 0}>
            Export CSV
          </Button>
          <Button component={Link} to="/dashboard/warning-dashboard/cro-pending-tasks" variant="text">
            Back to results
          </Button>
        </Box>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How many hours a stage can sit pending with the CRO before it's flagged. Verify basics is counted from the moment the lead
        was assigned to the CRO; every later stage is counted from the moment the previous stage was completed. Each stage has its
        own value - a stage with no value set yet falls back to its workflow SLA, or 24 hours if that isn't set either.
      </Typography>

      {loading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ my: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {!loading && error && <RetryNotice message={error} onRetry={load} />}
      {!loading && !error && notice && <Typography color="warning.main" sx={{ mb: 2 }}>{notice}</Typography>}

      {!loading && !error && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>Phase</strong></TableCell>
                <TableCell><strong>Stage</strong></TableCell>
                <TableCell align="right"><strong>Threshold (hours)</strong></TableCell>
                <TableCell align="right"><strong>Configured?</strong></TableCell>
                <TableCell><strong>Set by</strong></TableCell>
                <TableCell align="right"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.stage_no} hover>
                  <TableCell>{PHASE_LABEL[r.phase_no] || `Phase ${r.phase_no}`}</TableCell>
                  <TableCell>{r.stage_no} · {r.short_name}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={drafts[r.stage_no] ?? ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.stage_no]: e.target.value }))}
                      sx={{ width: 130 }}
                      {...numberField(drafts[r.stage_no] ?? '', 'Threshold', RULES.positiveHours, !!attempted[r.stage_no]).props}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={r.is_configured ? 'success.main' : 'text.secondary'}>
                      {r.is_configured ? 'Yes' : 'Default'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {/* one row per stage, overwritten on each save: the last person to save is who set the value now */}
                    <SetBy name={r.updated_by_name} at={r.updated_at} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!!notice || savingStage === r.stage_no || Number(drafts[r.stage_no]) === Number(r.threshold_hours)}
                      onClick={() => save(r.stage_no)}
                    >
                      {savingStage === r.stage_no ? 'Saving...' : 'Save'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </WarningPage>
  );
};

export default CroPendingTasksConfig;
